import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { center } from '@turf/turf';
import { isAxiosError } from 'axios';

import { admiPVAnalysisService } from './admi-pv-analysis.service';
import { admiPVCalculatorService } from './admi-pv-calculator.service';
import { admiBuildingsService } from 'src/features/real-estate/admi-buildings.service';

import {
    GeoLocation,
    LocationSearch,
    PvAnalysisBuildingMetadata,
    PvAnalysisVariationBuilding,
    PvAnalysisVariationCalculatorResult,
    PvAnalysisVariationRoofSegment,
} from './pv-analysis.dto';
import { Optimizer, PVResultDto, RoofSegmentDto, SupportedBuildingFunctionDtoWithName } from './pv-calculator.dto';
import { BuildingsMetadataPage, RealEstate } from 'src/features/real-estate/buildings.dto';
import { DEFAULT_PV_PARAMS, normalizeCostModel, OptimizerMode, PVInputParams } from './pv-input-types';
import { buildUniqueRoofSegmentId } from './pv-analysis.utils';
import { resolveBuildingFunctionCode } from 'src/common/buildingFunctions';
import { admiSolarPotentialsService, SolarPotentialSearch } from './admi-solar-potentials.service';
import { SolarPotentialSourceType } from './pv-solar-potentials.dto';
import { isProblemDetailTextOr } from 'src/common/ProblemDetail';

/**
 * Payload for `rejectWithValue`, whichever shape the axios layer produced.
 *
 * The response interceptor in `http-instance.ts` rejects with the bare
 * ProblemDetail body for every status except 401/403, so most HTTP errors reach
 * this helper already unwrapped and fall straight through. The `AxiosError`
 * branch is the exception, not the rule: it catches 401/403 and responseless
 * network failures, unwrapping them so the reducers get the server's `detail`
 * (or at least a string) rather than a non-serializable Error in the store.
 */
function rejectionValue(err: unknown): unknown {
    if (isAxiosError(err)) {
        return err.response?.data ?? err.message;
    }
    return err;
}

export const loadPvAnalysisVariantInitialView = createAsyncThunk(
    'pvAnalysis/loadPvAnalysisVariant',
    async (id: string | undefined) => {
        return Promise.all([
            id ? admiPVAnalysisService.getPVAnalysisVariant(id) : null,
            admiPVCalculatorService.getSupportedBuildingFunctions(),
            admiPVCalculatorService.getCostModels(),
        ]);
    },
);

export const loadRealEstateData = createAsyncThunk(
    'pvAnalysis/loadRealEstateData',
    async (dataSearch: LocationSearch, { rejectWithValue }) => {
        const mapToPvAnalysisBuildings = (dto: RealEstate): PvAnalysisVariationBuilding[] =>
            dto.buildings.map((building) => ({
                name: building.name,
                buildingFunctionCode: building.buildingFunction.code,
                code: building.code,
                geometry: building.geometry,
                roofSegments: [],
                source: 'N/a',
            }));

        try {
            return mapToPvAnalysisBuildings(await admiBuildingsService.loadBuildingsData(dataSearch));
        } catch (err) {
            return rejectWithValue(rejectionValue(err));
        }
    },
);

export const loadSolarPotentialsForBuilding = createAsyncThunk(
    'pvAnalysis/loadSolarPotentialsForBuilding',
    async (search: SolarPotentialSearch, { rejectWithValue }) => {
        try {
            return await admiSolarPotentialsService.loadSolarPotentials(search);
        } catch (err) {
            return rejectWithValue(rejectionValue(err));
        }
    },
);

export const calculatePvResults = createAsyncThunk(
    'pvAnalysis/calculatePvResults',
    async (
        params: {
            selectedBuildings: PvAnalysisVariationBuilding[];
            pvParams: PVInputParams;
        },
        thunkApi,
    ) => {
        // Map the selected buildings to the format the PV calculator expects
        const pvCalculatorRoofSegments = params.selectedBuildings
            .map((building, buildingIndex) =>
                building.roofSegments.map((segment, segmentIndex): RoofSegmentDto => {
                    return {
                        id: buildUniqueRoofSegmentId(buildingIndex, segmentIndex, segment),
                        building_id: `${buildingIndex}:${building.code}`,
                        direction: segment.azimuthDegrees,
                        roof_pitch: segment.tiltDegrees,
                        kwp: segment.kwp,
                        kwh_kwp: segment.kwhPerKwp,
                        area: segment.areaM2,
                    };
                }),
            )
            .flat();

        const [centerLong, centerLat] = center(params.selectedBuildings[0].geometry).geometry.coordinates;

        try {
            const pvParams = params.pvParams;
            const [optimizedResult, fullRoofResult] = await Promise.all([
                admiPVCalculatorService.doPVOptimization(
                    pvCalculatorRoofSegments,
                    pvParams.optimizer,
                    pvParams.buildingFunctionCode,
                    pvParams.loadProfiles,
                    pvParams.chpProductionProfile,
                    pvParams.fundingRate / 100,
                    pvParams.energyPrice,
                    pvParams.constrainFullRoofTo100KWp,
                    pvParams.costModel,
                    pvParams.productionOptimizerMode === OptimizerMode.Manual ? pvParams.targetKwp : undefined,
                    pvParams.batteryOptimizerMode === OptimizerMode.Manual ? pvParams.batteryKwpFactor : undefined,
                    centerLat,
                    centerLong,
                ),
                admiPVCalculatorService.doPVOptimization(
                    pvCalculatorRoofSegments,
                    Optimizer.FULL_ROOF,
                    pvParams.buildingFunctionCode,
                    pvParams.loadProfiles,
                    pvParams.chpProductionProfile,
                    0,
                    pvParams.energyPrice,
                    pvParams.constrainFullRoofTo100KWp,
                    pvParams.costModel,
                    undefined,
                    undefined,
                    centerLat,
                    centerLong,
                ),
            ]);

            return {
                optimizedResult,
                fullRoofResult,
                // Store these also so they don't change when the user changes the input
                optimizer: pvParams.optimizer,
                fundingRate: pvParams.fundingRate,
                baseUsageEstimated: admiPVCalculatorService.isBaseUsageEstimated(pvParams.loadProfiles),
            };
        } catch (e: unknown) {
            return thunkApi.rejectWithValue(admiPVCalculatorService.errorResponseToDescription(e));
        }
    },
);

export const loadPotentialBuildings = createAsyncThunk(
    'pvAnalysis/loadPotentialBuildings',
    async (dataSearch: LocationSearch, { rejectWithValue }) => {
        const mapToPvAnalysisBuildingsMetadata = (dto: BuildingsMetadataPage): PvAnalysisBuildingMetadata[] =>
            dto.content
                .filter((b) => b.geometry != null && b.filePath != null)
                .map((b) => ({
                    code: b.code,
                    function: b.buildingFunction?.code ?? null,
                    geometry: b.geometry as NonNullable<typeof b.geometry>,
                    filePath: b.filePath as NonNullable<typeof b.filePath>,
                    municipalityCode: b.municipality.code,
                }));

        try {
            return mapToPvAnalysisBuildingsMetadata(await admiBuildingsService.loadBuildingsMetaData(dataSearch));
        } catch (err) {
            return rejectWithValue(rejectionValue(err));
        }
    },
);

export const importBuilding = createAsyncThunk(
    'pvAnalysis/importBuilding',
    async (buildingMetadata: PvAnalysisBuildingMetadata, { rejectWithValue }) => {
        try {
            const response = await admiBuildingsService.importBuilding(
                buildingMetadata.filePath,
                buildingMetadata.code,
                buildingMetadata.municipalityCode,
            );
            return response;
        } catch (err) {
            return rejectWithValue(rejectionValue(err));
        }
    },
);

export interface State {
    location: GeoLocation | null;
    errorMessage: string;
    successMessage: string;
    buildings: PvAnalysisVariationBuilding[];
    buildingsMetadata: PvAnalysisBuildingMetadata[];
    selectedBuildingMetadata: PvAnalysisBuildingMetadata | null;
    highlightedBuilding: string;
    isMapLoading: boolean;
    isCalculationLoading: boolean;
    isImportBuildingLoading: boolean;
    showOnlySelectedBuildings: boolean;
    supportedBuildingFunctions?: SupportedBuildingFunctionDtoWithName[];
    costModels?: string[];
    sourceType: SolarPotentialSourceType;

    editedVariant: {
        pvAnalysisId?: string;
        pvAnalysisName: string;
        name: string;
        streetAddress: string;
        author?: string;
        buildings: PvAnalysisVariationBuilding[];
        pvParams: PVInputParams;
        calculatorResults: CalculatorResults | null;
    };
}

interface CalculatorResults {
    optimizedResult: PVResultDto;
    fullRoofResult: PVResultDto;
    optimizer: Optimizer;
    fundingRate: number;
    baseUsageEstimated: boolean;
}

const initialState: State = {
    location: {
        address: 'Oberbilker Allee 244, 40227 Düsseldorf',
        lat: 51.2108431,
        lng: 6.802931999999999,
    },
    errorMessage: '',
    successMessage: '',
    buildings: [],
    highlightedBuilding: '',
    isMapLoading: false,
    isCalculationLoading: false,
    showOnlySelectedBuildings: false,
    buildingsMetadata: [],
    selectedBuildingMetadata: null,
    isImportBuildingLoading: false,
    sourceType: SolarPotentialSourceType.GoogleSolar,

    editedVariant: {
        pvAnalysisName: '',
        name: '',
        streetAddress: '',
        buildings: [],
        pvParams: DEFAULT_PV_PARAMS,
        calculatorResults: null,
    },
};

const mapToPvResultDto = (result: PvAnalysisVariationCalculatorResult): PVResultDto => {
    return {
        design_kwp: result.kwp,
        design_kwh_per_year_ac: result.annualProduction,
        battery_capacity: result.batteryCapacity,
        load_summary: result.loadSummary,
        initial_investment_incl_funding: result.initialInvestmentInclFunding,
        initial_investment: result.initialInvestment,
        balance_30_years: result.balanceAfter30Years,
        rate_of_return: result.rateOfReturn,
        profit_loss_accumulated: result.profitLossAccumulated,
        amortization_years: result.amortizationYears,
        co2_savings: result.co2Savings,
        trees: result.trees,
        ratio_own_use: result.selfConsumptionRate,
        self_sufficiency_rate: result.selfSufficiencyRate,
        panel_count: result.panelCount,
        charts_day: result.chartsDay
            ? {
                  load: result.chartsDay.load,
                  pv_production: result.chartsDay.pvProduction,
                  self_consumption: result.chartsDay.selfConsumption,
                  battery_charge: result.chartsDay.batteryCharge,
                  battery_discharge: result.chartsDay.batteryDischarge,
              }
            : undefined,
        charts_year: result.chartsYear
            ? {
                  load: result.chartsYear.load,
                  pv_production: result.chartsYear.pvProduction,
              }
            : undefined,
    };
};

const pvAnalysisSlice = createSlice({
    name: 'pvAnalysis',
    initialState,
    reducers: {
        resetState: () => initialState,

        updatePvParams(state, action: PayloadAction<PVInputParams>) {
            state.editedVariant.pvParams = action.payload;
        },

        setLocation(state, action: PayloadAction<GeoLocation>) {
            state.location = action.payload;
        },

        setHighlightedBuilding(state, action: PayloadAction<string>) {
            state.highlightedBuilding = action.payload;
        },

        addBuildingToSelection(state, action: PayloadAction<PvAnalysisVariationBuilding>) {
            state.editedVariant.buildings.push(action.payload);
        },

        removeBuildingFromSelection(state, action: PayloadAction<PvAnalysisVariationBuilding>) {
            const selectedBuilding = action.payload;

            state.editedVariant.buildings = state.editedVariant.buildings.filter(
                (building) => building.code !== selectedBuilding.code,
            );
        },

        removeRoofSegmentFromBuildingSelection(
            state,
            action: PayloadAction<{
                selectedBuilding: PvAnalysisVariationBuilding;
                roofSegment: PvAnalysisVariationRoofSegment;
            }>,
        ) {
            const { selectedBuilding, roofSegment } = action.payload;

            const updatedBuilding = selectedBuilding.roofSegments.filter(
                (r: PvAnalysisVariationRoofSegment) => r !== roofSegment,
            );
            state.editedVariant.buildings = state.editedVariant.buildings.map((building) => {
                if (building.code === selectedBuilding.code) {
                    return {
                        ...building,
                        roofSegments: updatedBuilding,
                    };
                }
                return building;
            });
        },

        resetBuildingSelection(state) {
            state.editedVariant.buildings = [];
        },

        updateSelectedBuilding(state, action: PayloadAction<PvAnalysisVariationBuilding>) {
            state.editedVariant.buildings = state.editedVariant.buildings.map((building) => {
                if (building.code === action.payload.code) {
                    return action.payload;
                }
                return building;
            });
        },

        toggleShowOnlySelectedBuildings(state) {
            console.log('TOGGLE_SHOW_ONLY_SELECTED_BUILDINGS: ' + state.showOnlySelectedBuildings);
            state.showOnlySelectedBuildings = !state.showOnlySelectedBuildings;
        },

        setErrorMessage(state, action: PayloadAction<string>) {
            state.errorMessage = action.payload;
        },

        setSuccessMessage(state, action: PayloadAction<string>) {
            state.successMessage = action.payload;
        },

        setSelectedBuildingMetadata(state, action: PayloadAction<PvAnalysisBuildingMetadata>) {
            state.selectedBuildingMetadata = action.payload;
        },

        removeBuildingMetadata(state, action: PayloadAction<string>) {
            state.buildingsMetadata = state.buildingsMetadata.filter((building) => building.code !== action.payload);
        },

        setSourceType(state, action: PayloadAction<SolarPotentialSourceType>) {
            state.sourceType = action.payload;
            state.editedVariant.buildings = [];
        },

        resetImportBuildingFlow(state) {
            state.buildingsMetadata = [];
            state.selectedBuildingMetadata = null;
            state.errorMessage = '';
            state.successMessage = '';
            state.isImportBuildingLoading = false;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadPvAnalysisVariantInitialView.fulfilled, (state, action) => {
            const [variant, supportedBuildingFunctions, costModels] = action.payload;

            const buildingFunctionsWithSortedNames = supportedBuildingFunctions
                .map((buildingFunction) => ({
                    ...buildingFunction,
                    name: resolveBuildingFunctionCode(buildingFunction.code),
                }))
                .sort((a, b) => a.name.localeCompare(b.name));

            if (
                !variant ||
                !variant.mapScreenshot ||
                !variant.calculatorParams ||
                !variant.calculatorOptimizedResult ||
                !variant.calculatorFullRoofResult
            ) {
                return {
                    ...initialState,
                    supportedBuildingFunctions: buildingFunctionsWithSortedNames,
                    costModels,
                };
            }

            state.editedVariant = {
                pvAnalysisId: variant.pvAnalysisId,
                pvAnalysisName: variant.pvAnalysisName ?? '',
                name: variant.name,
                streetAddress: variant.streetAddress ?? '',
                author: variant.author,
                buildings: variant.buildings ?? [],
                pvParams: {
                    optimizer: variant.calculatorParams.optimizer,
                    buildingFunctionCode: variant.calculatorParams.buildingFunctionCode,
                    fundingRate: variant.calculatorParams.fundingRate,
                    energyPrice: variant.calculatorParams.energyPrice,
                    loadProfiles: variant.calculatorParams.loadProfiles,
                    costModel: normalizeCostModel(variant.calculatorParams.costModel),
                    productionOptimizerMode: variant.calculatorParams.targetKwp
                        ? OptimizerMode.Manual
                        : OptimizerMode.Auto,
                    targetKwp: variant.calculatorParams.targetKwp ?? 0,
                    batteryOptimizerMode: variant.calculatorParams.batteryCapacityFactor
                        ? OptimizerMode.Manual
                        : OptimizerMode.Auto,
                    batteryKwpFactor: variant.calculatorParams.batteryCapacityFactor ?? 0,
                    constrainFullRoofTo100KWp: variant.calculatorParams.limitTo100Kwp,
                    chpProductionProfile: null, // TODO
                },
                calculatorResults: {
                    optimizedResult: mapToPvResultDto(variant.calculatorOptimizedResult),
                    fullRoofResult: mapToPvResultDto(variant.calculatorFullRoofResult),
                    optimizer: variant.calculatorParams.optimizer,
                    fundingRate: variant.calculatorParams.fundingRate,
                    baseUsageEstimated: admiPVCalculatorService.isBaseUsageEstimated(
                        variant.calculatorParams.loadProfiles,
                    ),
                },
            };

            state.location = {
                lng: variant.mapScreenshot.center[0],
                lat: variant.mapScreenshot.center[1],
                zoom: variant.mapScreenshot.zoom,
                address: '',
            };

            state.supportedBuildingFunctions = buildingFunctionsWithSortedNames;
            state.costModels = costModels;
        });

        builder.addCase(loadRealEstateData.pending, (state) => {
            state.isMapLoading = true;
        });
        builder.addCase(loadRealEstateData.fulfilled, (state, action) => {
            state.isMapLoading = false;
            state.buildings = action.payload;
        });
        builder.addCase(loadRealEstateData.rejected, (state, action) => {
            state.isMapLoading = false;
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Error searching for buildings');
        });

        builder.addCase(loadPotentialBuildings.pending, (state) => {
            state.isImportBuildingLoading = true;
        });
        builder.addCase(loadPotentialBuildings.fulfilled, (state, action) => {
            state.isImportBuildingLoading = false;
            state.buildingsMetadata = action.payload;
        });
        builder.addCase(loadPotentialBuildings.rejected, (state, action) => {
            state.isImportBuildingLoading = false;
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Error loading potential buildings');
        });

        builder.addCase(loadSolarPotentialsForBuilding.pending, (state) => {
            state.isMapLoading = true;
        });
        builder.addCase(loadSolarPotentialsForBuilding.rejected, (state, action) => {
            state.isMapLoading = false;
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Error loading solar potentials');
        });
        builder.addCase(loadSolarPotentialsForBuilding.fulfilled, (state, action) => {
            const building = structuredClone({
                ...action.meta.arg.building,
                source: action.meta.arg.sourceType,
                roofSegments: action.payload,
            });

            if (!state.editedVariant.buildings.some((b) => b.code === building.code)) {
                state.editedVariant.buildings.push(building);
            }
            state.isMapLoading = false;

            // Invalidate calculator results, because they're not guaranteed to be valid anymore
            state.editedVariant.calculatorResults = null;

            // Pre-fill the building function if not set yet and it is supported
            const buildingFunctionIsSupported = state.supportedBuildingFunctions?.some(
                (bf) => bf.code === building.buildingFunctionCode,
            );
            if (
                !state.editedVariant.pvParams.buildingFunctionCode && // not set manually yet
                buildingFunctionIsSupported &&
                building.buildingFunctionCode
            ) {
                state.editedVariant.pvParams.buildingFunctionCode = building.buildingFunctionCode;
            }
        });

        builder.addCase(calculatePvResults.pending, (state) => {
            state.isCalculationLoading = true;
        });
        builder.addCase(calculatePvResults.fulfilled, (state, action) => {
            state.editedVariant.calculatorResults = action.payload;
            state.isCalculationLoading = false;
        });
        builder.addCase(calculatePvResults.rejected, (state, action) => {
            state.errorMessage = action.payload as string;
            state.editedVariant.calculatorResults = null;
            state.isCalculationLoading = false;
        });
        builder.addCase(importBuilding.pending, (state) => {
            state.isImportBuildingLoading = true;
            state.successMessage = '';
            state.errorMessage = '';
        });
        builder.addCase(importBuilding.fulfilled, (state) => {
            state.isImportBuildingLoading = false;
            state.successMessage = 'Building imported successfully';
        });
        builder.addCase(importBuilding.rejected, (state, action) => {
            state.errorMessage = isProblemDetailTextOr(action.payload, 'Error importing building');
            state.isImportBuildingLoading = false;
        });
    },
});

export const {
    resetState,
    updatePvParams,
    setLocation,
    setHighlightedBuilding,
    addBuildingToSelection,
    removeBuildingFromSelection,
    removeRoofSegmentFromBuildingSelection,
    resetBuildingSelection,
    updateSelectedBuilding,
    toggleShowOnlySelectedBuildings,
    setErrorMessage,
    setSuccessMessage,
    setSelectedBuildingMetadata,
    removeBuildingMetadata,
    setSourceType,
    resetImportBuildingFlow,
} = pvAnalysisSlice.actions;

export default pvAnalysisSlice.reducer;
